import {
  forwardRef,
  type ForwardRefExoticComponent,
  type HTMLProps,
} from "react"
import { twMerge } from "tailwind-merge"

type ColumnProps = HTMLProps<HTMLTableCellElement> & {
  className?: string
}

export const HeaderCell = forwardRef<HTMLTableCellElement, ColumnProps>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={twMerge("tt-table-header-cell", className)}
      {...props}
    />
  )
)
HeaderCell.displayName = "HeaderCell"

type TableHeaderProps = HTMLProps<HTMLTableSectionElement> & {
  className?: string
}

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={twMerge("tt-table-header", className)}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

type RowProps = HTMLProps<HTMLTableRowElement> & {
  className?: string
}

export const Row = forwardRef<HTMLTableRowElement, RowProps>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={twMerge("tt-table-row", className)}
      {...props}
    ></tr>
  )
)
Row.displayName = "Row"

type CellProps = HTMLProps<HTMLTableCellElement> & {
  className?: string
}

export const Cell = forwardRef<HTMLTableCellElement, CellProps>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={twMerge("tt-table-cell", className)} {...props} />
  )
)
Cell.displayName = "Cell"

type TableBodyProps = HTMLProps<HTMLTableSectionElement> & {
  className?: string
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={twMerge("tt-table-body", className)}
      {...props}
    />
  )
)
TableBody.displayName = "TableBody"

export type TableProps = HTMLProps<HTMLTableElement> & {
  className?: string
  density?: "small" | "medium" | "large" | "xlarge"
  rowBorderRadius?: "curved" | "none"
  rowHover?: boolean
  rowStyle?: "no-border" | "border" | "striped"
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      density = "medium",
      rowBorderRadius = "curved",
      rowHover = false,
      rowStyle = "border",
      ...props
    },
    ref
  ) => (
    <table
      ref={ref}
      data-density={density}
      data-row-border-radius={rowBorderRadius}
      data-row-hover={rowHover}
      data-row-style={rowStyle}
      className={twMerge("tt-table", className)}
      {...props}
    />
  )
) as ForwardRefExoticComponent<TableProps> & {
  Header: typeof TableHeader
  HeaderCell: typeof HeaderCell
  Row: typeof Row
  Cell: typeof Cell
  Body: typeof TableBody
}
Table.displayName = "Table"

Table.Header = TableHeader
Table.HeaderCell = HeaderCell
Table.Row = Row
Table.Cell = Cell
Table.Body = TableBody
